/**
 * Wellbeing (Notes & Wellbeing) — the disclaimer page for Soulumo.
 * Mar 6 2026.
 *
 * A calm, honest note about what Soulumo is and what it isn't,
 * with practical safety guidance for binaural beats and overnight
 * listening. Linked from the header, the footer, and the one-time
 * WellbeingAckBanner that appears the first time a user opens the
 * library.
 */
import React from "react";
import { goBack } from "../../components/SoulumoHeader";
import { useNavigate } from "react-router-dom";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";

const TOK = {
  bg: "radial-gradient(ellipse at 30% 15%, #1f3a5c 0%, #0d1f3a 35%, #060e1f 70%, #03060f 100%)",
  cream: "#f5e0ad",
  creamDim: "rgba(245,224,173,0.75)",
  creamMist: "rgba(245,224,173,0.42)",
  gold: "#e7c98a",
  goldSoft: "rgba(231,201,138,0.86)",
  goldMist: "rgba(231,201,138,0.24)",
  goldGlow: "rgba(231,201,138,0.10)",
};

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2
        className="text-[0.6rem] uppercase tracking-[0.4em] mb-4"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        · {title} ·
      </h2>
      <div
        className="space-y-4"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.08rem",
          color: TOK.creamDim,
          lineHeight: 1.65,
        }}
      >
        {children}
      </div>
    </section>
  );
}

export default function WellbeingPage() {
  const nav = useNavigate();

  return (
    <div
      className="min-h-screen relative overflow-x-hidden flex flex-col"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="soulumo-wellbeing-page"
    >
      <SoulumoHeader />

      <main className="relative z-10 flex-1 px-5 sm:px-8 pb-16">
        <div className="max-w-2xl mx-auto">
          {/* Hero */}
          <div className="text-center mt-6 mb-4">
            <p
              className="text-[0.6rem] uppercase tracking-[0.4em]"
              style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
            >
              · Notes & Wellbeing ·
            </p>
            <h1
              className="mt-4 font-light leading-tight"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(2rem, 6vw, 2.75rem)",
                color: TOK.cream,
              }}
            >
              A quiet, honest note before you listen.
            </h1>
          </div>

          <Section title="What Soulumo is">
            <p>
              Soulumo is a self-directed audio tool for reflection and
              inner practice. It lets you shape personal installations of
              your chosen words, your voice, calming music and gentle
              binaural frequencies — designed to be listened to at rest.
            </p>
            <p>
              It is intended to support your existing wellbeing and
              growth, not to replace it.
            </p>
          </Section>

          <Section title="What Soulumo is not">
            <p>
              Soulumo is not medical treatment, psychological therapy,
              diagnosis, or a substitute for the care of a qualified
              professional. It makes no promises about outcomes and
              cannot address serious physical or mental health
              conditions.
            </p>
            <p>
              If you are experiencing acute mental health difficulty,
              please speak with a qualified professional or contact a
              local crisis service. In the UK you can reach the
              Samaritans on <strong style={{ color: TOK.cream }}>116 123</strong>{" "}
              any time.
            </p>
          </Section>

          <Section title="Binaural beats — a gentle caution">
            <p>
              Our overnight and Signature installations layer very soft
              binaural tones — subtle differences between the left and
              right ear that can help the mind settle. These are widely
              used but should be treated thoughtfully.
            </p>
            <p>
              Please do <strong style={{ color: TOK.cream }}>not</strong>{" "}
              use Soulumo if any of the following apply, unless you have
              discussed it with your doctor first:
            </p>
            <ul className="list-none space-y-2 pl-1">
              {[
                "You have a history of seizures, epilepsy or a photosensitive condition",
                "You are pregnant",
                "You wear a pacemaker, cochlear implant or similar medical device",
                "You are under 18 and do not have guardian consent",
              ].map((t) => (
                <li key={t} className="flex gap-3 items-start">
                  <span style={{ color: TOK.gold, marginTop: 6 }}>·</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Overnight listening">
            <p>
              Overnight installations are designed to play quietly while
              you sleep. Please do not listen while driving, operating
              machinery, or in any setting where your full attention is
              needed.
            </p>
            <p>
              Set your volume gently — you should barely notice the audio
              once you have settled. Prolonged loud listening can damage
              hearing.
            </p>
          </Section>

          <Section title="Results vary">
            <p>
              How you experience Soulumo is personal. Some shifts arrive
              quickly, others quietly, and some never in the way you
              expected. We invite you to hold the experience lightly and
              return whenever it feels helpful.
            </p>
          </Section>

          <Section title="Your data">
            <p>
              Soulumo stores only what is needed to create and remember
              your installations — your account, your words, the audio
              you make. We never sell your data.
            </p>
            <p>
              For the full detail, see our{" "}
              <button
                onClick={() => nav("/soulumo/privacy")}
                className="underline"
                style={{ color: TOK.cream, fontFamily: "inherit" }}
                data-testid="wellbeing-privacy-link"
              >
                privacy notes
              </button>
              .
            </p>
          </Section>

          <Section title="About the music">
            <p>
              Every music bed inside Soulumo comes from our own licensed
              library — commissioned tracks and blanket-licensed
              production music. You cannot upload commercial music
              (Spotify, Apple Music, YouTube etc.) into an installation
              because the copyright wouldn't allow us to blend it with
              your voice.
            </p>
            <p>
              Read the full note at{" "}
              <button
                onClick={() => nav("/soulumo/music")}
                className="underline"
                style={{ color: TOK.cream, fontFamily: "inherit" }}
                data-testid="wellbeing-music-link"
              >
                About the music
              </button>
              .
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Anything unclear, anything concerning, anything you would
              like us to change — please write to us at{" "}
              <a
                href="mailto:hello@soulumo.app"
                className="underline"
                style={{ color: TOK.cream, fontFamily: "inherit" }}
                data-testid="wellbeing-contact-link"
              >
                hello@soulumo.app
              </a>
              .
            </p>
          </Section>

          {/* Return */}
          <div className="mt-14 text-center">
            <button
              onClick={() => goBack(nav, "/soulumo")}
              data-testid="wellbeing-return"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3"
              style={{
                background: "rgba(231,201,138,0.14)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.cream,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.68rem",
                letterSpacing: "0.28em",
                textTransform: "uppercase",
              }}
            >
              Return
            </button>
          </div>
        </div>
      </main>

      <SoulumoFooter />
    </div>
  );
}
